import { DEAL_STAGES, DEAL_TYPES, TRANSACTION_CREATED_FIELD } from 'resources/deal/deal.constants';
import { LISTEDKIT_TYPES, LISTED_KIT_TYPE_TO_STATUS } from './listedkit.constants';
import { COMMON_COMPUTED_FIELDS } from 'components/transaction/advancedFields/index.helpers';
import { uniqBy, groupBy } from 'lodash';


export const getListedKitType = (dealType, dealStage) => {
  switch (dealType) {
    case DEAL_TYPES.BUYER: {
      // we have only 1 type of under contract, pre-contract have more types (order is important)
      return dealStage === DEAL_STAGES.UNDER_CONTRACT
        ? LISTEDKIT_TYPES.BUYER_UNDER_CONTRACT
        : LISTEDKIT_TYPES.BUYER_PRE_CONTRACT;
    }
    case DEAL_TYPES.SELLER: {
      return dealStage === DEAL_STAGES.UNDER_CONTRACT
        ? LISTEDKIT_TYPES.SELLER_UNDER_CONTRACT
        : LISTEDKIT_TYPES.SELLER_PRE_CONTRACT;
    }

    default: break;
  }
};

export const sortListedKitsByTypeSequence = (listedKits, types) => {
  if (!listedKits || !types) return [];

  return types.flatMap((type) => listedKits.filter((listedKit) => listedKit.type === type));
};

const LISTED_KIT_TYPES_BY_DEAL_TYPE = {
  [DEAL_TYPES.BUYER]: [LISTEDKIT_TYPES.BUYER_UNDER_CONTRACT, LISTEDKIT_TYPES.BLANK],
  [DEAL_TYPES.SELLER]: [LISTEDKIT_TYPES.SELLER_UNDER_CONTRACT, LISTEDKIT_TYPES.BLANK],
};

const LISTED_KIT_TYPES_BY_DEAL_TYPE_AND_STAGE = {
  [DEAL_TYPES.BUYER]: {
    [DEAL_STAGES.PRE_CONTRACT]: [LISTEDKIT_TYPES.BUYER_PRE_CONTRACT, LISTEDKIT_TYPES.BLANK],
    [DEAL_STAGES.PRE_CONTRACT_HOME_SEARCH]: [LISTEDKIT_TYPES.BUYER_PRE_CONTRACT, LISTEDKIT_TYPES.BLANK],
    [DEAL_STAGES.PRE_CONTRACT_MAKE_OFFER]: [LISTEDKIT_TYPES.BUYER_PRE_CONTRACT, LISTEDKIT_TYPES.BLANK],
    [DEAL_STAGES.UNDER_CONTRACT]: [LISTEDKIT_TYPES.BUYER_UNDER_CONTRACT, LISTEDKIT_TYPES.BLANK],
  },
  [DEAL_TYPES.SELLER]: {
    [DEAL_STAGES.PRE_CONTRACT]: [LISTEDKIT_TYPES.SELLER_PRE_CONTRACT, LISTEDKIT_TYPES.BLANK],
    [DEAL_STAGES.PRE_CONTRACT_ACTIVE_LISTING]: [LISTEDKIT_TYPES.SELLER_PRE_CONTRACT, LISTEDKIT_TYPES.BLANK],
    [DEAL_STAGES.UNDER_CONTRACT]: [LISTEDKIT_TYPES.SELLER_UNDER_CONTRACT, LISTEDKIT_TYPES.BLANK],
  },
};

export const getListedKitsByDealTypeAndStage = (listedKits, dealType, dealStage) => {
  if (!listedKits) return [];

  const listedKitSequence = dealStage
    ? LISTED_KIT_TYPES_BY_DEAL_TYPE_AND_STAGE[dealType][dealStage]
    : LISTED_KIT_TYPES_BY_DEAL_TYPE[dealType];

  const filteredListedKits = sortListedKitsByTypeSequence(listedKits, listedKitSequence)
    .sort((a, b) => {
      if (!a?.name) {
        return -1;
      } if (!b?.name) {
        return 1;
      }
      return 0;
    });

  return filteredListedKits;
};

export const getSortedKitMilestonesAndAudits = (kit, kitMilestonesAndAudits) => {
  if (!kit || !kitMilestonesAndAudits || kitMilestonesAndAudits.length === 0) return [];

  // sort milestones and audits by created_on date initially so all other sortings will be based on created_on date
  const sortedByCreatedDate = kitMilestonesAndAudits
    .sort((first, second) => new Date(first.created_on) - new Date(second.created_on));

  switch (LISTED_KIT_TYPE_TO_STATUS[kit.type]) {
    case DEAL_STAGES.PRE_CONTRACT:
      return getSortedPreContract(sortedByCreatedDate);
    case DEAL_STAGES.UNDER_CONTRACT:
      return getSortedUnderContract(sortedByCreatedDate);
    case DEAL_STAGES.AFTER_CLOSE:
      return getSortedAfterClose(sortedByCreatedDate);
    default:
      return sortedByCreatedDate;
  }
};

const getSortedPreContract = (kitMilestonesAndAudits) => {
  // -- get milestones and audits WITH Due Date sorted by due_in_days after Transaction Created Date --
  const afterStartMilestonesWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      !milestoneOrAudit.isAudit &&
      milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_in_days !== null &&
      milestoneOrAudit.due_relative_date === TRANSACTION_CREATED_FIELD.NAME);

  const afterStartAuditsWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      milestoneOrAudit.isAudit &&
      milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_relative_date === TRANSACTION_CREATED_FIELD.NAME);

  const sortedByDueInDaysAfterStart = [...afterStartMilestonesWithDueDate, ...afterStartAuditsWithDueDate]
    .sort((first, second) => first.due_in_days - second.due_in_days);

  // -- get milestones WITHOUT Due Date --
  const noDueDate = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !milestoneOrAudit.isAudit && milestoneOrAudit.due_in_days === null);

  // -- get other milestones WITH Due Date sorted by due_in_days in scope of each Due Relative Date -->
  const sortedByDueInDaysAfterStartIds = sortedByDueInDaysAfterStart.map((milestoneOrAudit) => milestoneOrAudit.id);
  const noDueDateIds = noDueDate.map((milestoneOrAudit) => milestoneOrAudit.id);

  const otherMilestonesAndAudits = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !sortedByDueInDaysAfterStartIds.includes(milestoneOrAudit.id) && !noDueDateIds.includes(milestoneOrAudit.id));

  const sortedByDueInDaysOther = getSortedByDueInDaysOther(otherMilestonesAndAudits);

  return uniqBy([
    ...sortedByDueInDaysAfterStart,
    ...sortedByDueInDaysOther,
    ...noDueDate,
  ], 'id');
};

const getSortedUnderContract = (kitMilestonesAndAudits) => {
  // -- get milestones and audits WITH Due Date sorted by due_in_days after Under Contract Date --
  const afterContractMilestonesWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      !milestoneOrAudit.isAudit &&
      milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_in_days !== null &&
      milestoneOrAudit.due_relative_date === COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME);

  const afterContractAuditsWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      milestoneOrAudit.isAudit &&
      milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_relative_date === COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME);

  const sortedByDueInDaysAfterContract = [...afterContractMilestonesWithDueDate, ...afterContractAuditsWithDueDate]
    .sort((first, second) => first.due_in_days - second.due_in_days);

  // -- get milestones and audits WITH Due Date sorted by due_in_days before Closing Date --
  const beforeClosingMilestonesWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      !milestoneOrAudit.isAudit &&
      !milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_in_days !== null &&
      milestoneOrAudit.due_relative_date === COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME);

  const beforeClosingAuditsWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      milestoneOrAudit.isAudit &&
      !milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_relative_date === COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME);

  const sortedByDueInDaysBeforeClosing = [...beforeClosingMilestonesWithDueDate, ...beforeClosingAuditsWithDueDate]
    .sort((first, second) => second.due_in_days - first.due_in_days);

  // -- get milestones WITHOUT Due Date --
  const noDueDate = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !milestoneOrAudit.isAudit && milestoneOrAudit.due_in_days === null);

  // -- get other milestones WITH Due Date sorted by due_in_days in scope of each Due Relative Date -->
  const sortedByDueInDaysAfterContractIds = sortedByDueInDaysAfterContract.map((milestoneOrAudit) => milestoneOrAudit.id);
  const sortedByDueInDaysBeforeClosingIds = sortedByDueInDaysBeforeClosing.map((milestoneOrAudit) => milestoneOrAudit.id);
  const noDueDateIds = noDueDate.map((milestoneOrAudit) => milestoneOrAudit.id);

  const otherMilestones = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !sortedByDueInDaysAfterContractIds.includes(milestoneOrAudit.id) &&
    !sortedByDueInDaysBeforeClosingIds.includes(milestoneOrAudit.id) &&
    !noDueDateIds.includes(milestoneOrAudit.id));

  const sortedByDueInDaysOther = getSortedByDueInDaysOther(otherMilestones);

  return uniqBy([
    ...sortedByDueInDaysAfterContract,
    ...sortedByDueInDaysOther,
    ...sortedByDueInDaysBeforeClosing,
    ...noDueDate,
  ], 'id');
};

const getSortedAfterClose = (kitMilestonesAndAudits) => {
  // -- get milestones and audits WITH Due Date sorted by due_in_days after Closing Date --
  const afterCloseMilestonesWithDueDate = kitMilestonesAndAudits
    .filter((milestoneOrAudit) =>
      !milestoneOrAudit.isAudit &&
      milestoneOrAudit.is_after_action &&
      milestoneOrAudit.due_in_days !== null &&
      milestoneOrAudit.due_relative_date === COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME);

  const sortedByDueInDaysAfterClose = [...afterCloseMilestonesWithDueDate]
    .sort((first, second) => first.due_in_days - second.due_in_days);


  // -- get milestones WITHOUT Due Date --
  const noDueDate = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !milestoneOrAudit.isAudit && milestoneOrAudit.due_in_days === null);

  // -- get other milestones WITH Due Date sorted by due_in_days in scope of each Due Relative Date -->
  const sortedByDueInDaysAfterCloseIds = sortedByDueInDaysAfterClose.map((milestoneOrAudit) => milestoneOrAudit.id);
  const noDueDateIds = noDueDate.map((milestoneOrAudit) => milestoneOrAudit.id);

  const otherMilestones = kitMilestonesAndAudits.filter((milestoneOrAudit) =>
    !sortedByDueInDaysAfterCloseIds.includes(milestoneOrAudit.id) &&
    !noDueDateIds.includes(milestoneOrAudit.id));

  const sortedByDueInDaysOther = getSortedByDueInDaysOther(otherMilestones);

  return uniqBy([
    ...sortedByDueInDaysAfterClose,
    ...sortedByDueInDaysOther,
    ...noDueDate,
  ], 'id');
};

const getSortedByDueInDaysOther = (otherMilestonesAndAudits) => {
  if (!otherMilestonesAndAudits) return [];

  const groupedOther = groupBy(otherMilestonesAndAudits, 'due_relative_date');

  const sortedByDueInDaysOther = Object.values(groupedOther).map((group) => {
    // sort by due_in_days from highest to lowest when is_after_action is false
    const sortedBeforeAction = group
      .filter(item => !item.is_after_action)
      .sort((first, second) => second.due_in_days - first.due_in_days);
    // sort by due_in_days from lowest to highest when is_after_action is true
    const sortedAfterAction = group
      .filter(item => item.is_after_action)
      .sort((first, second) => first.due_in_days - second.due_in_days);

    return [...sortedBeforeAction, ...sortedAfterAction];
  });

  return sortedByDueInDaysOther.flat();
};
